import { DOCUMENT, Location } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import {
  getOriginalPath,
  isNodaFormUrl,
  isPathAvailableWithTranslations,
  isSearchEngine,
} from '@core/handlers/utils.handlers';
import { CookiesService } from '@noda-lib/utils/cookies';
import { PlatformService, UtilsStorageService } from '@noda-lib/utils/services';
import { NODA_WEB_LINK } from '@shared/constants/noda-links';
import { AVAILABLE_LANGS, TranslateLang } from '@translate/constants/available-langs';

import { PRODUCT_PAGES } from '../api/constants/pages.constants';
import { EnUTMFields, EnUTMMediumStatics } from '../api/enums/utm-parameters.enums';

interface IParsedUrl {
  queryParams: string;
  route: string;
}

@Injectable({
  providedIn: 'root',
})
export class UtmParametersService {
  private utmMedium: string;
  private utmCampaign: string;
  private utmCookieName: string = 'firstUserEntryUTM';

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private location: Location,
    private storageService: UtilsStorageService,
    private cookiesService: CookiesService,
    private platformService: PlatformService,
  ) {}

  public init(): void {
    if (this.platformService.isBrowser()) {
      this.setSessionUTMParams();

      if (!this.checkUtmCookie()) {
        this.setCookieUTMParams();
      }
    }
  }

  public openHrefWithUtmParameters(href: string, isSelfTarget: boolean = true): void {
    if (href) {
      const target: string = isSelfTarget ? '_self' : '_blank';
      this.document.defaultView.open(this.addUtmParamsToUrl(href), target);
    }
  }

  public addUtmParamsToUrl(url: string): string {
    const newUrl: URL = url.startsWith('https') ? new URL(url) : new URL(NODA_WEB_LINK + url);

    switch (true) {
      case isNodaFormUrl(url): {
        const utmSearchParams: URLSearchParams = new URLSearchParams(this.getUtmCookie());
        utmSearchParams.forEach((value: string, key: string) => {
          newUrl.searchParams.set(key, value);
        });
        newUrl.hash = 'contact';

        return newUrl.href;
      }

      default:
        return url;
    }
  }

  private setUTMParams(isCookie: boolean): void {
    const { route, queryParams } = this.parseURL();

    if (queryParams) {
      if (isCookie) {
        this.saveUtmCookie(queryParams);
      }

      return;
    }

    const normalizedRoute: string = this.normalizeRoute(route);
    this.defineUtmParams(normalizedRoute);

    const utmParamsString = this.createUtmParamsString();
    if (isCookie) {
      this.saveUtmCookie(utmParamsString);
    }
  }

  private setCookieUTMParams(): void {
    this.setUTMParams(true);
  }

  private setSessionUTMParams(): void {
    this.setUTMParams(false);
  }

  private createUtmParamsString(): string {
    const referrer: string = this.document.referrer;
    let utmSource: string = 'direct';
    let utmMedium: string = this.utmMedium;

    if (referrer) {
      switch (true) {
        case isSearchEngine(referrer): {
          utmSource = 'seo';
          break;
        }

        default: {
          utmSource = 'referral';
          utmMedium = new URL(referrer).hostname;
        }
      }
    }

    const utmParams: URLSearchParams = new URLSearchParams();
    utmParams.set(EnUTMFields.UTM_SOURCE, utmSource);
    utmParams.set(EnUTMFields.UTM_MEDIUM, utmMedium);

    if (this.utmCampaign) {
      utmParams.set(EnUTMFields.UTM_CAMPAIGN, this.utmCampaign);
    }

    return utmParams.toString();
  }

  private normalizeRoute(route: string): string {
    const segments: string[] = route.split('/').filter((segment: string) => segment);

    return AVAILABLE_LANGS.includes(segments[0] as TranslateLang)
      ? segments.slice(1).join('/')
      : segments.join('/');
  }

  private defineUtmParams(route: string): void {
    if (isPathAvailableWithTranslations(PRODUCT_PAGES, route)) {
      this.utmCampaign = getOriginalPath(route);
      this.utmMedium = EnUTMMediumStatics.PRODUCTS;
    } else if (this.isTwoLevelLink(route)) {
      this.setTwoLevelLinkUTMParams(route);
    } else {
      this.utmMedium = route || EnUTMMediumStatics.HOME;
    }
  }

  private setTwoLevelLinkUTMParams(route: string): void {
    const [utmMedium, utmCampaign] = route.split('/');

    this.utmMedium = utmMedium;
    this.utmCampaign = utmCampaign;
  }

  private parseURL(): IParsedUrl {
    const [route, queryParams] = this.location.path().replace('/', '').split('?');

    return {
      route,
      queryParams,
    };
  }

  private isTwoLevelLink(route: string): boolean {
    return route.includes('/');
  }

  private checkUtmCookie(): boolean {
    return this.cookiesService.has(this.utmCookieName);
  }

  private saveUtmCookie(value: string): void {
    this.cookiesService.set(this.utmCookieName, value, { expires: 90, path: '/' });
  }

  private getUtmCookie(): string {
    return this.cookiesService.get(this.utmCookieName);
  }
}
